import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ABAST_NAV, ADMIN_NAV, COMM_NAV, DOC_NAV, GERENCIA_NAV, ISO_NAV, MAIN_NAV, OPS_NAV, PREV_NAV, COMPRAS_NAV, RRHH_NAV } from "./navConfig";
import { api } from "@/lib/api";

const SECTIONS = [
  ["gerencia", "Gerencia", GERENCIA_NAV], ["rrhh", "RR.HH.", RRHH_NAV], ["abast", "Abastecimiento", ABAST_NAV],
  ["ops", "Operaciones", OPS_NAV], ["prev", "Prevención", PREV_NAV], ["compras", "Compras y Flota", COMPRAS_NAV],
  ["iso", "ISO", ISO_NAV], ["admin", "Administración", ADMIN_NAV], ["docs", "Documentación", DOC_NAV],
];
const ALL_PATHS = [MAIN_NAV, COMM_NAV, ...SECTIONS.map((s) => s[2])].flat().map((i) => i.to);
const isParent = (to) => to === "/" || ALL_PATHS.some((p) => p.startsWith(`${to}/`));
const matches = (to, path) => path === to || path.startsWith(`${to}/`);
const sectionOf = (path) => {
  let best = null, len = 0;
  SECTIONS.forEach(([key, , items]) => items.forEach((i) => {
    if (i.to !== "/" && matches(i.to, path) && i.to.length > len) { best = key; len = i.to.length; }
  }));
  return best;
};

const Item = ({ item, onNavigate, count }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={isParent(item.to)} data-testid={item.testid} onClick={onNavigate}
      className={({ isActive }) => `syc-side-link ${isActive ? "is-active" : ""}`}>
      <Icon size={17} /> <span className="flex-1">{item.label}</span>
      {count > 0 && <span data-testid={`${item.testid}-badge`} className="rounded-full bg-[#5CD500] px-1.5 text-[10px] font-bold text-[#0f1a08]">{count}</span>}
    </NavLink>
  );
};

const useChatUnread = (pathname) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/chat/unread").then((r) => setN(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [pathname]);
  return n;
};

const Section = ({ id, title, open, onToggle, children }) => (
  <div className="mb-1">
    <button type="button" data-testid={`nav-section-${id}`} aria-expanded={open} onClick={onToggle}
      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#2f3a2b] hover:bg-[#f0f2ee] hover:text-[#258F00] transition-colors">
      {title}
      <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="mt-0.5 mb-2" data-testid={`nav-section-${id}-items`}>{children}</div>}
  </div>
);

export const SidebarContent = ({ onNavigate }) => {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => sectionOf(pathname));
  useEffect(() => { const s = sectionOf(pathname); if (s) setOpen(s); }, [pathname]);
  const unread = useChatUnread(pathname);
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-3 pb-5 mb-4 border-b border-[#e4e8e1]">
        <div className="syc-logo">S&amp;C</div>
        <div>
          <div className="font-extrabold tracking-[0.08em] text-[#258F00]">SYC CONTROL</div>
          <div className="text-[11px] text-[#5b6656]">Plataforma corporativa</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto pr-1">
        <div className="mb-3">
          <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#2f3a2b]">General</div>
          {MAIN_NAV.map((i) => <Item key={i.to} item={i} onNavigate={onNavigate} />)}
        </div>
        {SECTIONS.map(([key, title, items]) => {
          const visible = items.filter((i) => !i.perm || can(i.perm));
          if (!visible.length) return null;
          return (
            <Section key={key} id={key} title={title} open={open === key} onToggle={() => setOpen(open === key ? null : key)}>
              {visible.map((i) => <Item key={i.to} item={i} onNavigate={onNavigate} />)}
            </Section>
          );
        })}
        <div className="mt-3" data-testid="nav-comm-group">
          {COMM_NAV.map((i) => <Item key={i.to} item={i} onNavigate={onNavigate} count={i.badge ? unread : 0} />)}
        </div>
      </nav>
    </div>
  );
};
